import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Search, X, User, FileText, Briefcase, FileSpreadsheet, Receipt, UserCheck } from "lucide-react";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/global-search?q=${encodeURIComponent(q)}`);
        setResults(data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const close = () => { setOpen(false); setQ(""); setResults(null); };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm text-slate-500"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="hidden md:inline">Search everything...</span>
        <kbd className="hidden md:inline ml-2 text-xs px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={close} data-testid="global-search-modal">
          <div className="max-w-3xl mx-auto mt-20 bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center border-b border-slate-100 px-4">
              <Search className="w-5 h-5 text-slate-400" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search leads, clients, projects, files, invoices, designers..."
                className="flex-1 py-4 px-3 outline-none text-base"
                data-testid="global-search-input"
              />
              <button onClick={close} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {loading && <div className="p-6 text-center text-slate-500 text-sm">Searching...</div>}
              {!loading && !results && q.length < 2 && <div className="p-6 text-center text-slate-400 text-sm">Type at least 2 characters</div>}
              {!loading && results && (
                <Results results={results} onClick={close} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ icon: Icon, title, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="text-xs font-semibold text-slate-500 uppercase px-3 py-1 flex items-center gap-2"><Icon className="w-3 h-3" /> {title} ({items.length})</div>
      <div className="space-y-1">{items.map(render)}</div>
    </div>
  );
}

function Row({ to, onClick, primary, secondary, badge }) {
  return (
    <Link to={to} onClick={onClick} className="block px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid="search-result-row">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-slate-900 text-sm">{primary}</div>
          {secondary && <div className="text-xs text-slate-500">{secondary}</div>}
        </div>
        {badge && <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{badge}</span>}
      </div>
    </Link>
  );
}

function Results({ results, onClick }) {
  const total = ["leads", "clients", "projects", "files", "invoices", "designers"].reduce((s, k) => s + (results[k]?.length || 0), 0);
  if (total === 0) return <div className="p-6 text-center text-slate-500 text-sm">No matches found</div>;
  return (
    <div>
      <Section icon={User} title="Leads" items={results.leads} render={(l) => (
        <Row key={l.id} to="/admin/leads" onClick={onClick} primary={l.name || "(no name)"} secondary={`${l.phone || ""} · ${l.email || ""}`} badge={l.pipeline_stage} />
      )} />
      <Section icon={UserCheck} title="Clients" items={results.clients} render={(c) => (
        <Row key={c.id} to="/admin/orders" onClick={onClick} primary={c.name} secondary={`${c.phone || ""} · ${c.email}`} badge={c.city} />
      )} />
      <Section icon={Briefcase} title="Projects" items={results.projects} render={(p) => (
        <Row key={p.id} to={`/admin/projects/${p.id}`} onClick={onClick} primary={p.package_title || p.id} secondary={`₹${p.amount} · ${p.id.slice(0, 8)}`} badge={p.workflow_stage} />
      )} />
      <Section icon={FileText} title="Files" items={results.files} render={(f) => (
        <Row key={f.id} to={`/admin/projects/${f.order_id}`} onClick={onClick} primary={f.file_name} secondary={`${f.folder} · v${f.version}`} />
      )} />
      <Section icon={Receipt} title="Invoices" items={results.invoices} render={(i) => (
        <Row key={i.id} to="/admin/invoices" onClick={onClick} primary={i.invoice_no} secondary={`${i.client?.name || ""} · ₹${i.grand_total}`} badge={i.status} />
      )} />
      <Section icon={UserCheck} title="Designers" items={results.designers} render={(d) => (
        <Row key={d.id} to="/admin/designers" onClick={onClick} primary={d.name} secondary={`${d.phone || ""} · ${d.email}`} />
      )} />
    </div>
  );
}
